/* Enterprise Inquiry Form
 *
 * Replaces the mailto: link on the Landing page's Enterprise pricing card.
 * Submits to POST /api/waitlist/enterprise (existing endpoint). On success
 * shows a thank-you confirmation page.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { ArrowRight, CheckCircle, Buildings } from "@phosphor-icons/react";

const REQUIREMENT_OPTIONS = [
  "Enterprise Calling",
  "Contact Center",
  "Multi-location Deployment",
  "SIP Trunking",
  "Dedicated Account Manager",
  "SSO",
  "API Integration",
  "Migration Assistance",
  "Compliance",
  "Other",
];

const COMPANY_SIZES = [
  "1-10 employees", "11-50 employees", "51-200 employees",
  "201-500 employees", "501-1,000 employees", "1,001-5,000 employees", "5,000+ employees",
];

export default function EnterpriseInquiry() {
  const [f, setF] = useState({
    company_name: "", country: "", website: "", industry: "",
    contact_name: "", work_email: "", phone: "",
    seats_estimate: "", company_size: "",
    requirements: [], additional_notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleReq = (r) => setF((p) => ({
    ...p,
    requirements: p.requirements.includes(r)
      ? p.requirements.filter((x) => x !== r)
      : [...p.requirements, r],
  }));

  const canSubmit = f.company_name.trim() && f.country.trim() &&
    f.contact_name.trim() && f.work_email.includes("@");

  async function submit() {
    if (!canSubmit || busy) return;
    setBusy(true); setErr("");
    try {
      await api.post("/waitlist/enterprise", {
        company_name:     f.company_name.trim(),
        country:          f.country.trim(),
        website:          f.website.trim() || null,
        industry:         f.industry.trim() || null,
        contact_name:     f.contact_name.trim(),
        work_email:       f.work_email.trim().toLowerCase(),
        phone:            f.phone.trim() || null,
        seats_estimate:   f.seats_estimate.trim() || null,
        company_size:     f.company_size || null,
        requirements:     f.requirements,
        additional_notes: f.additional_notes.trim() || null,
      });
      setSubmitted(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-white" data-testid="enterprise-thankyou">
        <header className="border-b border-[var(--border)] px-8 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5"><Logo size={36} /></Link>
        </header>
        <div className="max-w-2xl mx-auto px-8 py-24 text-center">
          <CheckCircle size={72} weight="fill" className="text-[var(--success)] mx-auto mb-8" />
          <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ INQUIRY RECEIVED</div>
          <h1 className="font-display font-black text-[40px] leading-[1.0] mb-4">Thank you.</h1>
          <p className="text-[15px] text-[var(--text-secondary)] mb-10 leading-relaxed">
            The Hello by Flow Enterprise team has your inquiry and will be in touch
            within one business day at <strong>{f.work_email}</strong>.
          </p>
          <Link to="/" className="btn-primary inline-flex items-center gap-2" data-testid="enterprise-back-home">
            Back to home <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="enterprise-inquiry">
      <header className="border-b border-[var(--border)] px-8 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5"><Logo size={36} /></Link>
        <div className="text-[13px] text-[var(--text-secondary)]">Already a customer? <Link to="/login" className="font-semibold text-[var(--accent)]">Log in</Link></div>
      </header>

      <div className="max-w-3xl mx-auto px-8 py-12">
        <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ ENTERPRISE</div>
        <h1 className="font-display font-black text-[40px] leading-[1.0] mb-3">Tell us about your business.</h1>
        <p className="text-[14px] text-[var(--text-secondary)] mb-12 max-w-2xl">
          Custom contracts, dedicated infrastructure, and priority SLAs. Fill in
          the form and our Enterprise team will reach out within one business day.
        </p>

        <Section icon={Buildings} title="Company information">
          <Field label="Company name *" value={f.company_name} onChange={(v) => set("company_name", v)} testid="ei-company_name" />
          <Field label="Country *" value={f.country} onChange={(v) => set("country", v)} testid="ei-country" />
          <Field label="Company website (optional)" value={f.website} onChange={(v) => set("website", v)} testid="ei-website" />
          <Field label="Industry (optional)" value={f.industry} onChange={(v) => set("industry", v)} testid="ei-industry" />
        </Section>

        <Section title="Contact information">
          <Field label="Full name *" value={f.contact_name} onChange={(v) => set("contact_name", v)} testid="ei-contact_name" />
          <Field label="Work email *" type="email" value={f.work_email} onChange={(v) => set("work_email", v)} testid="ei-work_email" />
          <Field label="Phone number (optional)" value={f.phone} onChange={(v) => set("phone", v)} testid="ei-phone" />
        </Section>

        <Section title="Organization details">
          <Field label="Estimated Communication Seats" value={f.seats_estimate} onChange={(v) => set("seats_estimate", v)} testid="ei-seats_estimate" placeholder="e.g. 25 or 250" />
          <div>
            <label className="label">Company size</label>
            <select className="input" value={f.company_size} onChange={(e) => set("company_size", e.target.value)} data-testid="ei-company_size">
              <option value="">Select…</option>
              {COMPANY_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </Section>

        <div className="mb-10">
          <div className="font-display font-bold text-[16px] mb-4">Requirements</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {REQUIREMENT_OPTIONS.map((r) => (
              <label key={r} className={`flex items-center gap-3 border p-3 cursor-pointer transition-colors ${f.requirements.includes(r) ? "border-[var(--accent)] bg-[var(--bg-sidebar)]" : "border-[var(--border)] hover:bg-[var(--bg-sidebar)]"}`}>
                <input type="checkbox" checked={f.requirements.includes(r)} onChange={() => toggleReq(r)} className="w-4 h-4 accent-[var(--accent)]" data-testid={`ei-req-${r.toLowerCase().replace(/\s+/g, "-")}`} />
                <span className="text-[13px]">{r}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mb-10">
          <label className="label">Additional requirements</label>
          <textarea
            className="input min-h-[140px] w-full"
            value={f.additional_notes}
            onChange={(e) => set("additional_notes", e.target.value)}
            placeholder="Tell us more about your use case, compliance needs, timelines, or integrations."
            data-testid="ei-additional_notes"
          />
        </div>

        {err && <div className="mb-6 text-[13px] text-[var(--danger)]" data-testid="ei-error">{err}</div>}

        <div className="flex justify-between items-center">
          <Link to="/" className="btn-ghost inline-flex items-center gap-2">Cancel</Link>
          <button
            disabled={!canSubmit || busy}
            onClick={submit}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
            data-testid="ei-submit"
          >
            {busy ? "Sending…" : "Submit inquiry"} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="mb-10">
      <div className="flex items-center gap-2.5 mb-4">
        {Icon && <Icon size={18} weight="duotone" className="text-[var(--accent)]" />}
        <div className="font-display font-bold text-[16px]">{title}</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">{children}</div>
    </div>
  );
}
function Field({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="input w-full" data-testid={testid} placeholder={placeholder} />
    </div>
  );
}
